import * as React from 'react';
import {
  MdAdd,
  MdPerson,
  MdBlock,
  MdCheckCircle,
  MdClose,
  MdDelete,
  MdEdit,
  MdPauseCircle,
  MdSearch,
  MdVisibility,
} from 'react-icons/md';
import DataGrid from '../../Components/Table/DataGrid';
import DeleteModel from '../../Components/Common/DeleteModel';
import { useNavigate } from 'react-router-dom';
import { APPRoutes } from '../../Utils/Route';
import { getAllStaffs, deleteStaff } from '../../Services/StaffService';
import { useAuth } from '../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes, isSuperAdminUser } from '../../Utils/Permissions';
import { toast } from 'react-toastify';
import PageTitle from '../../Components/Common/PageTitle';
import SearchableDropdown from '../../Components/Subscription/SearchableDropdown';

const STATUS_OPTIONS = ['All', 'Active', 'Inactive'];
const STATUS_DROPDOWN_OPTIONS = STATUS_OPTIONS.map((option) => ({
  value: option,
  label: option,
}));
const STATUS_BADGE_STYLE = {
  Active: { backgroundColor: 'var(--hc-status-success-bg)', color: 'var(--hc-status-success-text)' },
  Inactive: { backgroundColor: 'var(--hc-status-inactive-bg)', color: 'var(--hc-status-inactive-text)' },
};

const getStatusLabel = (row) => {
  if (!row) return 'Unknown';
  return row.status_name || row.StatusName || (row.status_id === 1 || row.StatusId === 1 ? 'Active' : 'Inactive');
};

const getStaffName = (row) => {
  if (!row) return '-';
  const name = `${row.first_name || row.FirstName || ''} ${row.last_name || row.LastName || ''}`.trim();
  return name || row.email || 'Unknown Staff';
};

export default function StaffsList() {
  const navigate = useNavigate();
  const { token, permissions, decodedToken } = useAuth();
  const userPermissionCodes = React.useMemo(
    () => resolveUserPermissionCodes({ permissions, decodedToken }),
    [permissions, decodedToken],
  );

  const canViewStaff = permissionHas(userPermissionCodes, PermissionCodes.STAFFS_VIEW, decodedToken);
  const canCreateStaff = permissionHas(userPermissionCodes, PermissionCodes.STAFFS_CREATE, decodedToken);
  const canEditStaff = permissionHas(userPermissionCodes, PermissionCodes.STAFFS_EDIT, decodedToken);
  const canDeleteStaff = permissionHas(userPermissionCodes, PermissionCodes.STAFFS_DELETE, decodedToken);

  const isSuperAdmin = isSuperAdminUser(decodedToken);

  const [search, setSearch] = React.useState('');
  const [status, setStatus] = React.useState('All');
  const [rows, setRows] = React.useState([]);
  const [statusSummary, setStatusSummary] = React.useState({ total: 0, active: 0, inactive: 0 });
  const [loading, setLoading] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState('');

  const handleCreateStaff = React.useCallback(() => {
    navigate(APPRoutes.STAFFS_FORM);
  }, [navigate]);

  const loadStaffs = React.useCallback(async () => {
    if (!token) return;

    setLoading(true);
    try {
      const response = await getAllStaffs(token);
      const payload = response?.data ?? response;
      const staffs = Array.isArray(payload?.staffs) ? payload.staffs : [];
      const stats = payload?.stats || {
        total: staffs.length,
        active: staffs.filter((staff) => getStatusLabel(staff) === 'Active').length,
        inactive: staffs.filter((staff) => getStatusLabel(staff) === 'Inactive').length,
      };

      setRows(staffs);
      setStatusSummary({
        total: Number(stats.total || staffs.length),
        active: Number(stats.active || staffs.filter((staff) => getStatusLabel(staff) === 'Active').length),
        inactive: Number(stats.inactive || staffs.filter((staff) => getStatusLabel(staff) === 'Inactive').length),
      });
    } catch (error) {
      console.error('Failed to load staff members:', error);
      toast.error('Unable to fetch staff members. Please try again.');
      setRows([]);
      setStatusSummary({ total: 0, active: 0, inactive: 0 });
    } finally {
      setLoading(false);
    }
  }, [token]);

  React.useEffect(() => {
    if (token) {
      loadStaffs();
    }
  }, [token, loadStaffs]);

  const handleDeleteClick = React.useCallback((row) => {
    setDeleteError('');
    setDeleteTarget(row);
  }, []);

  const handleDeleteClose = React.useCallback(() => {
    if (deleteLoading) return;
    setDeleteTarget(null);
    setDeleteError('');
  }, [deleteLoading]);

  const handleDeleteConfirm = React.useCallback(async () => {
    if (!deleteTarget?.id || !token) return;

    setDeleteLoading(true);
    setDeleteError('');
    try {
      const response = await deleteStaff(deleteTarget.id, token);
      if (response?.is_success) {
        await loadStaffs();
        setDeleteTarget(null);
        toast.success(response?.message || 'Staff member deleted successfully.');
      } else if (response?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
        setDeleteError('Internal Server Error. Please Contact Support Team.');
      } else {
        setDeleteError(response?.message || 'Failed to delete the staff member. Please try again.');
      }
    } catch (error) {
      console.error(error);
      setDeleteError('Something went wrong while deleting the staff member.');
    } finally {
      setDeleteLoading(false);
    }
  }, [deleteTarget, token, loadStaffs]);

  const filteredRows = React.useMemo(() => {
    const term = search.trim().toLowerCase();

    return rows.filter((row) => {
      const rowStatus = getStatusLabel(row);
      const matchesStatus = status === 'All' || rowStatus === status;
      const fullName = getStaffName(row).toLowerCase();
      const matchesSearch =
        !term ||
        fullName.includes(term) ||
        (row.organization_name ?? row.OrganizationName ?? '').toLowerCase().includes(term) ||
        (row.account_name ?? row.AccountName ?? '').toLowerCase().includes(term) ||
        (row.email ?? '').toLowerCase().includes(term) ||
        (row.title ?? '').toLowerCase().includes(term);

      return matchesStatus && matchesSearch;
    });
  }, [rows, search, status]);

  const stats = React.useMemo(
    () => [
      {
        label: 'Total Staff',
        value: statusSummary.total,
        icon: <MdPerson size={22} color="var(--hc-icon-blue-text)" />,
        iconBg: 'var(--hc-icon-blue-bg)',
        accentClass: 'border-primary',
        valueClass: 'text-primary',
      },
      {
        label: 'Active',
        value: statusSummary.active,
        icon: <MdCheckCircle size={22} color="var(--hc-status-success-text)" />,
        iconBg: 'var(--hc-icon-green-bg)',
        accentClass: 'border-success',
        valueClass: 'text-success',
      },
      {
        label: 'Inactive',
        value: statusSummary.inactive,
        icon: <MdPauseCircle size={22} color="var(--hc-status-warning-text)" />,
        iconBg: 'var(--hc-status-warning-bg)',
        accentClass: 'border-secondary',
        valueClass: 'text-secondary',
      },
    ],
    [statusSummary],
  );

  const columns = React.useMemo(() => {
    const cols = [
      {
        id: 'staff',
        label: 'Staff',
        minWidth: 220,
        format: (_value, row) => canViewStaff ? (
          <button
            type="button"
            className="btn p-0 text-start text-dark border-0 bg-transparent"
            style={{ font: 'inherit' }}
            onClick={() =>
              navigate(APPRoutes.STAFFS_VIEW.replace(':id', row.id), {
                state: { staff: row },
              })
            }
          >
            {getStaffName(row)}
          </button>
        ) : (
          <span className="text-dark">{getStaffName(row)}</span>
        ),
      },
      {
        id: 'organization_name',
        label: 'Organization',
        minWidth: 180,
        format: (value, row) => value ?? row.OrganizationName ?? '-',
      },
    ];

    if (isSuperAdmin) {
      cols.push({
        id: 'account_name',
        label: 'Client',
        minWidth: 180,
        format: (value, row) => value ?? row.AccountName ?? '-',
      });
    }

    cols.push(
      {
        id: 'status_name',
        label: 'Status',
        minWidth: 120,
        align: 'center',
        format: (_value, row) => {
          const statusName = getStatusLabel(row);
          return (
            <span
              className="badge rounded-pill fw-bolder px-3 py-2 text-center"
              style={{
                minWidth: '96px',
                fontWeight: 1000,
                ...(STATUS_BADGE_STYLE[statusName] || {
                  backgroundColor: 'var(--hc-status-inactive-bg)',
                  color: 'var(--hc-status-inactive-text)',
                }),
              }}
            >
              {statusName}
            </span>
          );
        },
      },
    );

    if (canViewStaff || canEditStaff || canDeleteStaff) {
      cols.push({
        id: 'actions',
        label: 'Actions',
        minWidth: 120,
        align: 'center',
        format: (_value, row) => (
          <div className="d-flex align-items-center justify-content-center gap-2">
            {canViewStaff ? (
              <button
                type="button"
                title="View"
                className="btn btn-sm p-1 lh-1 text-primary border-0 rounded-2"
                style={{ backgroundColor: 'var(--hc-status-info-bg)' }}
                onClick={() =>
                  navigate(APPRoutes.STAFFS_VIEW.replace(':id', row.id), {
                    state: { staff: row },
                  })
                }
              >
                <MdVisibility size={16} />
              </button>
            ) : null}
            {canEditStaff ? (
              <button
                type="button"
                title="Edit"
                className="btn btn-sm p-1 lh-1 hc-text-primary border-0 rounded-2"
                style={{ backgroundColor: 'var(--hc-icon-blue-bg)' }}
                onClick={() => navigate(APPRoutes.STAFFS_EDIT.replace(':id', row.id))}
              >
                <MdEdit size={16} />
              </button>
            ) : null}
            {canDeleteStaff ? (
              <button
                type="button"
                title="Delete"
                className="btn btn-sm p-1 lh-1 text-danger border-0 rounded-2"
                style={{ backgroundColor: 'var(--hc-status-danger-bg)' }}
                onClick={() => handleDeleteClick(row)}
              >
                <MdDelete size={16} />
              </button>
            ) : null}
          </div>
        ),
      });
    }

    return cols;
  }, [
    navigate,
    handleDeleteClick,
    canViewStaff,
    canEditStaff,
    canDeleteStaff,
    isSuperAdmin,
  ]);

  return (
    <>
      <PageTitle title="Staffs - Aegis 360"/>
      <div className="d-flex flex-md-row flex-column align-items-start justify-content-between mb-4">
        <div>
          <h4 className="fw-bold mb-1 hc-text-primary fs-3">Staff Management</h4>
          <p className="text-secondary mb-0 small">
            Manage all staff members, organization assignments, and account access.
          </p>
        </div>
        {canCreateStaff ? (
          <button
            onClick={handleCreateStaff}
            type="button"
            className="btn hc-bg-primary mt-md-0 mt-2 w-md-auto btn-sm text-white fw-semibold d-flex align-items-center justify-content-center gap-1 px-3 py-2 rounded-3 text-nowrap"
          >
            <MdAdd size={20} />
            <span>Create Staff</span>
          </button>
        ) : null}
      </div>

      {loading ? (
        <div className="row g-3 mb-3">
          {[1, 2, 3].map((_, i) => (
            <div key={i} className="col-12 col-sm-6 col-lg-4">
              <div className="bg-white p-4 rounded-4 shadow d-flex align-items-center gap-3 h-100">
                <div className="skeleton position-relative overflow-hidden rounded-2" style={{ width: 44, height: 44, borderRadius: 12 }} />
                <div className="w-100">
                  <div className="skeleton mb-2" style={{ height: 16, width: '70%' }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 24, width: '45%' }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="row g-3 mb-3">
          {stats.map((stat) => (
            <div key={stat.label} className="col-12 col-sm-6 col-lg-4">
              <div className="position-relative overflow-hidden bg-white p-4 rounded-4 shadow d-flex align-items-center gap-3 h-100 hc-list-kpi-card">
                <div className={`position-absolute start-0 top-0 h-100 border-start border-5 ${stat.accentClass}`} />
                <div className="d-inline-flex align-items-center justify-content-center rounded-3" style={{ width: 44, height: 44, backgroundColor: stat.iconBg }}>
                  {stat.icon}
                </div>
                <div className="d-flex flex-column fw-bold lh-sm">
                  <span className="text-dark" style={{ fontSize: '0.85rem' }}>
                    {stat.label}
                  </span>
                  <span className={`fw-bold fs-5 ${stat.valueClass}`}>
                    {stat.value}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-3 shadow-lg p-3 p-md-4">
        <div className="row g-2 mb-3">
          <div className="col-12 col-md-9">
            <div className="position-relative">
              <span
                className="position-absolute top-50 translate-middle-y d-inline-flex align-items-center justify-content-center text-secondary pe-none"
                style={{ left: '12px' }}
              >
                <MdSearch size={18} />
              </span>
              <input
                type="text"
                className="form-control form-control-sm hc-input"
                placeholder="Search staff by name, organization, email, or account"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                style={{ paddingLeft: '36px', paddingRight: '36px', height: '40px' }}
              />
              {search ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearch('')}
                  className="btn btn-link bg-danger p-0 position-absolute top-50 translate-middle-y text-light rounded-circle"
                  style={{ right: '8px', lineHeight: 0 }}
                >
                  <MdClose size={18} />
                </button>
              ) : null}
            </div>
          </div>

          <div className="col-12 col-md-3">

               <SearchableDropdown
                  placeholder="Select status"
                  options={STATUS_DROPDOWN_OPTIONS}
                  value={status}
                  onChange={setStatus}
                  showSearch={true}
                />

            {/* <FormControl fullWidth size="small">
              <InputLabel id="staff-status-label">Status</InputLabel>
              <Select
                labelId="staff-status-label"
                label="Status"
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl> */}
          </div>
        </div>

        <div className="bg-white p-3 rounded shadow">
          {loading ? (
            <div>
              {[...Array(8)].map((_, i) => (
                <div key={i} className="d-flex gap-3 align-items-center mb-3">
                  <div className="skeleton position-relative overflow-hidden rounded-2 w-25" style={{ height: 16 }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '20%' }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '15%' }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '20%' }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '10%' }} />
                </div>
              ))}
            </div>
          ) : (
            <DataGrid
              columns={columns}
              rows={filteredRows}
              defaultRowsPerPage={10}
              stickyHeader
              getRowId={(row) => row.id}
            />
          )}
        </div>
      </div>

      <DeleteModel
        isOpen={Boolean(deleteTarget)}
        title="Delete Staff"
        entityLabel="staff member"
        itemName={getStaffName(deleteTarget || {})}
        description="You are about to remove this staff member from the platform. This action is permanent."
        confirmText="Delete Staff"
        isLoading={deleteLoading}
        errorMessage={deleteError}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
}
